import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';

const Callback = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    const processOAuthCallback = async () => {
      try {
        const searchParams = new URLSearchParams(location.search);
        const code = searchParams.get('code');
        const errorDescription = searchParams.get('error_description');
        
        // Se há erro, mostrar e redirecionar
        if (errorDescription) {
          setError(decodeURIComponent(errorDescription));
          setLoading(false);
          
          toast({
            variant: "destructive",
            title: "Erro de autenticação",
            description: decodeURIComponent(errorDescription),
          });
          
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // ✅ MÉTODO CORRETO: Usar exchangeCodeForSession do Supabase
        if (code) {
          console.log('Processing auth code with Supabase...');
          
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          
          if (exchangeError) {
            console.error('Error exchanging code for session:', exchangeError);
            throw exchangeError;
          }
          
          if (data.session && data.user) {
            console.log('User authenticated successfully:', data.user.email);
            
            // Verificar e atribuir role se necessário
            const { data: existingRoles } = await supabase
              .from('user_roles')
              .select('role_id')
              .eq('user_id', data.user.id);
              
            if (!existingRoles || existingRoles.length === 0) {
              const { data: userRole } = await supabase
                .from('roles')
                .select('id')
                .eq('name', 'User')
                .single();
                
              if (userRole) {
                await supabase
                  .from('user_roles')
                  .insert({
                    user_id: data.user.id,
                    role_id: userRole.id,
                    created_by: data.user.id,
                    updated_by: data.user.id
                  });
              }
            }
            
            toast({
              title: "Login bem-sucedido",
              description: "Você foi autenticado com sucesso via Microsoft.",
            });
            
            // Redirecionar para home
            navigate('/');
          } else {
            throw new Error('Session não foi criada após troca do código');
          }
        } else {
          throw new Error('Nenhum código de autorização recebido');
        }
      } catch (err: any) {
        console.error('Error in OAuth callback:', err);
        setError(err.message || 'Erro durante a autenticação.');
        setLoading(false);
        
        toast({
          variant: "destructive",
          title: "Erro de autenticação",
          description: err.message || "Erro durante a autenticação.",
        });
        
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    processOAuthCallback();
  }, [location.search, navigate, toast]);

  if (loading && !error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
        <p className="text-lg">Completando autenticação...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-extrabold text-destructive mb-4">Erro de Autenticação</h1>
          <p className="text-muted-foreground mb-4">{error}</p>
          <p className="text-sm text-muted-foreground">Redirecionando para a página de login...</p>
        </div>
      </div>
    );
  }

  return null;
};

export default Callback; 